/**
 * Pulse Sparkline — React Query hook for sparkline time-series data
 *
 * Calls zaviago_backend.api.dashboard_sparkline.get_pulse_sparkline
 * which returns daily orders count and revenue sum for 7/30/90 days.
 *
 * Designed to be lazy-loaded: pass `enabled: false` until the main
 * pulse tiles have loaded, so sparkline fetching never blocks tile render.
 */

import { useQuery, keepPreviousData, type UseQueryResult } from "@tanstack/react-query";
import { frappeMethod } from "@/lib/erp/utils";

// ── Types matching backend response shape ──────────────────

export type PulsePeriod = "7d" | "30d" | "90d" | "mtd" | "ytd";

export interface SparklinePoint {
  date: string; // "YYYY-MM-DD"
  value: number;
}

export interface SparklineSummary {
  orders_total: number;
  orders_avg: number;
  orders_prev_total: number;
  orders_delta: number;
  orders_delta_pct: number | null;
  revenue_total: number;
  revenue_avg: number;
  revenue_prev_total: number;
  revenue_delta: number;
  revenue_delta_pct: number | null;
  products_total: number;
  products_avg: number;
  products_prev_total: number;
  products_delta: number;
  products_delta_pct: number | null;
  // SO-based revenue for AOV (avoids SINV/SO count mismatch)
  orders_revenue_total?: number;
  orders_revenue_prev_total?: number;
}

export interface SparklineResponse {
  orders: SparklinePoint[];
  revenue: SparklinePoint[];
  products: SparklinePoint[];
  summary: SparklineSummary;
}

// ── Fetcher ───────────────────────────────────────────────

async function fetchPulseSparkline(
  period: PulsePeriod,
  storefront?: string | null,
  fromDate?: string | null,
  toDate?: string | null
): Promise<SparklineResponse> {
  const args: Record<string, string> = { period };
  if (storefront) args.storefront = storefront;
  // Both bounds are required for a custom window; the backend ignores partial input.
  if (fromDate && toDate) {
    args.from_date = fromDate;
    args.to_date = toDate;
  }
  return frappeMethod<SparklineResponse>(
    "zaviago_backend.api.dashboard_sparkline.get_pulse_sparkline",
    args
  );
}

// ── Hook ───────────────────────────────────────────────────

export function usePulseSparkline(
  period: PulsePeriod,
  enabled: boolean = true,
  storefront?: string | null,
  fromDate?: string | null,
  toDate?: string | null
): UseQueryResult<SparklineResponse, Error> {
  const custom = fromDate && toDate ? `${fromDate}:${toDate}` : "preset";
  return useQuery({
    queryKey: ["pulse-sparkline", period, storefront ?? "all", custom],
    queryFn: () => fetchPulseSparkline(period, storefront, fromDate, toDate),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    placeholderData: keepPreviousData,
    enabled,
  });
}
